import { useCallback, useEffect, useMemo, useState } from "react";
import { DocumentContentActions } from "../../documentsApi";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useNavigate } from "react-router-dom";
import { DocumentTest, DocumentTestAssignment, DocumentTestRoleType, loadDocumentAllTests, loadDocumentSectionTests } from "../../documentTests/documentTestsApi";
import { unwrapResult } from "@reduxjs/toolkit";
import produce from "immer";
import { LoadingContainer } from "../../../../app/LoadingContainer";
import { ErrorContainer } from "../../../../app/ErrorContainer";
import { Box, FormControlLabel, IconButton, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { Localized } from "@fluent/react";
import { showDocComments } from "../../documentSlice";
import { LpField } from "../../../../app/components/LpField";
import { EditIconButton } from "../../../../app/components/iconButtons/EditIconButton";
import { If } from "../../../../app/If";
import { AllCommentsCounter } from "../../CommentsCounter";
import CommentIcon from '@mui/icons-material/Comment';
import EditTestScheduleDialog from "./dialogs/EditTestScheduleDialog";
import { PrintPreviewDocumentScheduleTestView } from "./PrintPreviewDocumentScheduleTestView";

export interface DocumentScheduleTestSectionViewProps {
    documentId: number
    sectionId: string
    actions: DocumentContentActions[],
    enablePreview?: boolean
}

function hasAction(
    actions: DocumentContentActions[],
    action: DocumentContentActions
): boolean {
    return actions.includes(action);
}

type DialogState
    = "none"
    | "addTest"
    | "editTest"
    | "deleteTest"


export const DocumentScheduleTestSectionView = ({ documentId, sectionId, actions, enablePreview = false }: DocumentScheduleTestSectionViewProps) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);
    const [documentTests, setDocumentTests] = useState<DocumentTest[]>([]);
    const [isPreview, setIsPreview] = useState(false);
    const [dialog, setDialog] = useState<DialogState>("none")
    const [selectedTest, setSelectedTest] = useState<DocumentTest | null>(null);
    const showComments = useAppSelector(state => showDocComments(state, sectionId))
    const fDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString() : "—");

    const rows = useMemo(
        () => {
            const arr = [...documentTests];
            arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            return arr;
        },
        [documentTests]
    );

    const pickByRole = (
        assignments: DocumentTestAssignment[] | undefined,
        role: DocumentTestRoleType
    ) => assignments?.find(a => a.role === role);

    const renderPerson = (a?: { name?: string | null; email?: string | null; companyName?: string | null }) => {
        if (!a) return "—";
        if (a.name && a.email) return `${a.name} (${a.email})`;
        return a.name || a.email || "—";
    };

    useEffect(() => {
        setIsLoading(true)
        dispatch(loadDocumentAllTests({ documentId: documentId }))
            .then(unwrapResult)
            .then((versions: DocumentTest[]) => {
                setDocumentTests(versions);
            })
            .catch((error) => { console.error(error); setIsError(true); setError(error); })
            .finally(() => setIsLoading(false))
    }, [documentId, sectionId, dispatch])

    // ======================================================
    //  Handlers
    // ======================================================

    const handleOpenTest = (documentTestId: number) => {
        const test = documentTests.find(t => t.id === documentTestId) || null;
        setSelectedTest(test);
        setDialog("editTest");
    };

    const handleDialogCancelClick = useCallback(() => {
        setDialog("none")
        setSelectedTest(null);
    }, [])

    const onSuccessfullySave = (test: DocumentTest) => {
        setDocumentTests(produce(draft => {
            if (draft !== undefined) {
                const index = draft.findIndex(q => q.id === test.id);

                if (index !== -1) {
                    draft[index] = test;
                } else {
                    draft.push(test);
                }
            }
        }));
        setDialog("none")
    };

    // ======================================================
    //  Render
    // ======================================================
    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }


    return (
        <>
            {enablePreview && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isPreview}
                                onChange={() => setIsPreview((v) => !v)}
                            />
                        }
                        label={
                            <Localized id="print-preview">
                                <span>_Print preview</span>
                            </Localized>
                        }
                    />
                </Box>
            )}

            {enablePreview && isPreview ? (
                <PrintPreviewDocumentScheduleTestView tests={rows} />
            ) : (
                <>
                    <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "hidden" }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell width={56}><b>#</b></TableCell>
                                    <TableCell><b>Test</b></TableCell>
                                    <TableCell width={160}><b>Plan start</b></TableCell>
                                    <TableCell width={160}><b>Plan koniec</b></TableCell>

                                    <TableCell width={120} align="right" />
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                            Brak testów.
                                        </TableCell>
                                    </TableRow>
                                )}

                                {rows.map((row, index) => {
                                    const name = row.testSpecification?.name || "—";
                                    return (
                                        <TableRow hover key={`${row.id}-${row.id}`}>
                                            <TableCell>
                                                <LpField value={index + 1} />
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 260 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
                                                {row.sectionName && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Sekcja: {row.sectionName}
                                                    </Typography>
                                                )}
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="body2">{fDate(row.plannedStart)}</Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="body2">{fDate(row.plannedEnd)}</Typography>
                                            </TableCell>

                                            <TableCell align="right">
                                                <If condition={hasAction(actions, "Edit")}>
                                                    <>
                                                        <EditIconButton
                                                            tooltip={<Localized id='edit'>_edit</Localized>}
                                                            onClick={() => handleOpenTest?.(row.id)}
                                                        />

                                                    </>
                                                </If>
                                                <If condition={showComments}>
                                                    <AllCommentsCounter sectionId={row.sectionId ?? ''} contextGuid={row.guid}>
                                                        <Tooltip title={<Localized id='comments'>Komentarze</Localized>}>
                                                            <IconButton
                                                                sx={{
                                                                    color: "action.active",
                                                                }}
                                                                onClick={() => handleOpenTest?.(row.id)}
                                                            >
                                                                <CommentIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </AllCommentsCounter>
                                                </If>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer >
                    {
                        dialog === "editTest" && selectedTest &&
                        <EditTestScheduleDialog
                            testSpecification={selectedTest}
                            onCancel={handleDialogCancelClick}
                            onSave={onSuccessfullySave}
                            documentId={documentId}
                            sectionId={sectionId}
                        />
                    }
                </>
            )}
        </>
    );
};

export default DocumentScheduleTestSectionView;